// WhatsApp reminders card (WA-CLINIC-UI): the NETWORK layer. Only RPC calls, nothing else: the card never reads a table (every wa_* table is default-deny) and never uses .maybeSingle().
// Every call destructures { data, error }, logs failures with the prefix used across the app, and returns a plain result: { ok, data, error: {code, hint, message}|null, unavailable }.
// `unavailable: true` means the FUNCTION DOES NOT EXIST yet on this database (PostgREST PGRST202, Postgres 42883, or the "could not find the function" message): the packages behind the card are applied in windows,
// so the card must hide itself cleanly until they are. It never throws and never retries.
// Requests (plan rev 3, section 4): opening the card = wa_activation_status (1), plus wa_integration_status ONLY when the first answer reports an integration (at most 2). Each action = exactly 1 request.
// No timer, no polling, no realtime subscription (the app's listener limit): the caller refreshes on open, on tab-visibility regain and after an action.

import { needsIntegrationRead, resolveState } from './state.js';

const MISSING_RE = /could not find the function|function .* does not exist|no function matches/i;

export function normalizeError(error) {
  if (!error) return null;
  const code = String(error.code || ''), message = String(error.message || ''), hint = error.hint ? String(error.hint) : null;
  return { code, hint, message, unavailable: code === 'PGRST202' || code === '42883' || MISSING_RE.test(message) };
}

// createApi(getSupa): getSupa returns the Supabase client (in the app: window.HUD._supa, an OBJECT, not a function); the tests pass a fake.
export function createApi(getSupa, log) {
  const say = log || ((...a) => console.error(...a));
  async function rpc(name, args) {
    const supa = getSupa();
    if (!supa || typeof supa.rpc !== 'function') return { ok: false, data: null, error: { code: 'NO_CLIENT', hint: null, message: 'no client' }, unavailable: false };
    let res;
    try { res = await supa.rpc(name, args); }
    catch (e) { say('❌ ' + name + ':', e); return { ok: false, data: null, error: { code: 'NETWORK', hint: null, message: String(e && e.message || e) }, unavailable: false }; }
    const { data, error } = res || {};
    if (error) {
      const err = normalizeError(error);
      if (!err.unavailable) say('❌ ' + name + ':', error);          // a function that is not applied yet is expected, not an error to log
      return { ok: false, data: null, error: err, unavailable: err.unavailable };
    }
    return { ok: true, data: data === undefined ? null : data, error: null, unavailable: false };
  }
  return {
    rpc,
    getActivation: clinic => rpc('wa_activation_status', { p_clinic: clinic }),
    getIntegration: clinic => rpc('wa_integration_status', { p_clinic: clinic }),
    requestActivation: (clinic, pref, bestTime) => rpc('wa_activation_request', { p_clinic: clinic, p_contact_pref: pref, p_best_time: bestTime || null }),
    cancelActivation: clinic => rpc('wa_activation_cancel', { p_clinic: clinic }),
    saveTiming: (clinic, m) => rpc('wa_integration_set_timing', { p_clinic: clinic, p_mode: m.p_mode, p_timing: m.p_timing, p_send_hour: m.p_send_hour }),
    // only the Shabbat flag is sent: the hour and the therapist flag are NULL = "keep" (the narrowed wa_integration_set_settings)
    saveShabbat: (clinic, on) => rpc('wa_integration_set_settings', { p_clinic: clinic, p_send_hour: null, p_shabbat_skip: !!on, p_include_therapist_first: null }),
    setEnabled: (clinic, enabled) => rpc('wa_integration_set_enabled', { p_clinic: clinic, p_enabled: !!enabled }),
    disconnect: clinic => rpc('wa_disconnect', { p_clinic: clinic }),
    // open the card: returns { unavailable, error, act, integ, resolved }. Never more than 2 requests.
    async load(clinic) {
      const a = await rpc('wa_activation_status', { p_clinic: clinic });
      if (a.unavailable) return { unavailable: true, error: null, act: null, integ: null, resolved: resolveState(null, null) };
      if (!a.ok) return { unavailable: false, error: a.error, act: null, integ: null, resolved: resolveState(null, null) };
      let integ = null;
      if (needsIntegrationRead(a.data)) {
        const i = await rpc('wa_integration_status', { p_clinic: clinic });
        if (i.ok) integ = i.data;                                                  // a failed second read leaves integ null: the resolver then refuses to guess for a disabled clinic
        else if (!i.unavailable) return { unavailable: false, error: i.error, act: a.data, integ: null, resolved: resolveState(a.data, null) };
      }
      return { unavailable: false, error: null, act: a.data, integ, resolved: resolveState(a.data, integ) };
    }
  };
}

// the app instance: window.HUD._supa is the client OBJECT (never call it)
export const api = createApi(() => (typeof window !== 'undefined' && window.HUD ? window.HUD._supa : null));
